export function isEmptyString(value: string | null | undefined): value is null | undefined | '' {
  return value == null || value.trim() === '';
}

/**
 * Whether `host` belongs to `target` by dot-boundary suffix — `host` equals
 * `target` exactly, or `host` is a subdomain of `target`. Both inputs are
 * lower-cased before comparing. The dot prefix avoids accidental tail matches
 * like `nodemycompany.com` matching `mycompany.com`.
 *
 * Examples:
 *   matchesDomainSuffix('mycompany.com', 'mycompany.com')       // true
 *   matchesDomainSuffix('mail.mycompany.com', 'mycompany.com')  // true
 *   matchesDomainSuffix('nodemycompany.com', 'mycompany.com')   // false
 */
export function matchesDomainSuffix(host: string, target: string): boolean {
  if (isEmptyString(host) || isEmptyString(target)) return false;
  const hostLower = host.toLowerCase();
  const targetLower = target.toLowerCase();
  return hostLower === targetLower || hostLower.endsWith(`.${targetLower}`);
}

/**
 * Uppercase the first character of `value`, leaving the rest as-is.
 * Returns the input unchanged when it's empty.
 */
export function capitalize(value: string): string {
  if (value === '') return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Title-case a string: replace underscores with dashes, then uppercase the
 * first letter of every word (word boundaries are start-of-string, whitespace,
 * dash, and slash). Leaves already-uppercase letters untouched, so acronyms
 * like "UA push" become "UA Push". Mirrors Ember's `titleize` helper.
 */
export function titleize(value: string): string {
  return value.replace(/_/g, '-').replace(/(?:^|\s|-|\/)\S/g, (match) => match.toUpperCase());
}

/**
 * Base64-encode a string that may contain non-Latin1 characters. Plain
 * `btoa` throws on code points above 0xFF; escaping to UTF-8 byte sequences
 * first keeps it safe for unicode search terms.
 *
 * Direct port of the Ember `b64EncodeUnicode` util — the message-library
 * search endpoint expects the `search` query param base64-encoded this way.
 */
export function base64EncodeUnicode(value: string): string {
  return btoa(
    encodeURIComponent(value).replace(/%([\dA-F]{2})/g, (_match, hex: string) =>
      String.fromCharCode(Number.parseInt(hex, 16)),
    ),
  );
}

/**
 * Join words into an English list: "a", "a and b", "a, b, and c" (Oxford
 * comma). Mirrors Ember's `join-words` helper.
 */
export function joinWords(words: string[]): string {
  if (words.length <= 1) {
    return words[0] ?? '';
  }
  if (words.length === 2) {
    return `${words[0]} and ${words[1]}`;
  }
  return `${words.slice(0, -1).join(', ')}, and ${words.at(-1)}`;
}
